// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { validateMontoTasa } from "utils/validations";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "50%" },
  overflowY: "auto",
  p: 3,
};

const SAVE_DECLARACION_JURADA = gql`
  mutation saveDeclaracionJurada($input: DeclaracionJuradaInput!) {
    saveDeclaracionJurada(input: $input) {
      id
      tasa
      montoTasa
    }
  }
`;

const GET_TASAS = gql`
  query getTasasForModal {
    tasas {
      categoria
      importe
      porcentaje
    }
  }
`;

export default function ModalCambiarTasa({ open, handleClose, refetch, declaracion }) {
  const [formData, setFormData] = useState({
    tasa: "",
    montoTasa: "",
  });
  const { handleSnackbar } = useContext(MessageManager);

  const [saveDeclaracion, { loading: loadingSave }] = useMutation(SAVE_DECLARACION_JURADA);
  const { data: dataTasas } = useQuery(GET_TASAS, {
    fetchPolicy: "no-cache",
  });

  const calcularMontoPorCategoria = (categoria) => {
    if (!categoria) return "";

    const tasas = dataTasas?.tasas || [];
    const tasaSeleccionada = tasas.find((item) => item.categoria === categoria);
    if (!tasaSeleccionada) return "";

    if (categoria.length === 1) {
      return tasaSeleccionada.importe ?? "";
    }

    if (categoria.startsWith("1.")) {
      const tasaBase = tasas.find((item) => item.categoria === "C");
      if (
        tasaBase?.importe !== null &&
        tasaBase?.importe !== undefined &&
        tasaSeleccionada?.porcentaje !== null &&
        tasaSeleccionada?.porcentaje !== undefined
      ) {
        return (Number(tasaBase.importe) * Number(tasaSeleccionada.porcentaje)) / 100;
      }
    }

    if (categoria.startsWith("2.")) {
      const tasaBase = tasas.find((item) => item.categoria === "E");
      if (
        tasaBase?.importe !== null &&
        tasaBase?.importe !== undefined &&
        tasaSeleccionada?.porcentaje !== null &&
        tasaSeleccionada?.porcentaje !== undefined
      ) {
        return (Number(tasaBase.importe) * Number(tasaSeleccionada.porcentaje)) / 100;
      }
    }

    return tasaSeleccionada.importe ?? "";
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
    if (name === "tasa") {
      setFormData((prev) => ({
        ...prev,
        tasa: value,
        montoTasa: calcularMontoPorCategoria(value),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    if (!validateMontoTasa(formData.montoTasa)) {
      handleSnackbar("Ingrese un monto de tasa válido", "error");
      return;
    }
    saveDeclaracion({
      variables: {
        input: {
          id: declaracion?.id,
          tasa: formData.tasa,
          montoTasa: Number(formData.montoTasa),
        },
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Tasa actualizada correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al actualizar la tasa", "error");
      });
  };

  useEffect(() => {
    if (declaracion && open) {
      setFormData({
        tasa: declaracion.tasa || "",
        montoTasa: declaracion.montoTasa ?? "",
      });
    } else {
      setFormData({
        tasa: "",
        montoTasa: "",
      });
    }
  }, [declaracion, open]);

  useEffect(() => {
    if (!open || !formData.tasa) return;
    if (formData.montoTasa !== "" && formData.montoTasa !== null && formData.montoTasa !== undefined)
      return;

    const montoCalculado = calcularMontoPorCategoria(formData.tasa);
    if (montoCalculado === "" || montoCalculado === null || montoCalculado === undefined) return;

    setFormData((prev) => ({
      ...prev,
      montoTasa: montoCalculado,
    }));
  }, [open, formData.tasa, dataTasas]);

  const tasasDisponibles = [
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
    { value: "F", label: "F" },
    { value: "G", label: "G" },
    { value: "H", label: "H" },
    { value: "I", label: "I" },
    { value: "1.A", label: "1.A" },
    { value: "1.B", label: "1.B" },
    { value: "1.C", label: "1.C" },
    { value: "1.D", label: "1.D" },
    { value: "1.E", label: "1.E" },
    { value: "2.A", label: "2.A" },
    { value: "2.B", label: "2.B" },
    { value: "2.C", label: "2.C" },
    { value: "2.D", label: "2.D" },
    { value: "2.E", label: "2.E" },
  ];

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">Cambiar Tasa de Declaración Jurada</SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                {/* Información de la declaración */}
                <Grid item xs={12}>
                  <SoftTypography variant="h6" fontWeight="bold" color="dark">
                    Información de la Declaración
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Legajo
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput value={declaracion?.id || ""} disabled />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <InputLabel>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Empresa
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput value={declaracion?.empresa?.razonSocial || ""} disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Tasa Actual
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput value={declaracion?.tasa || "Sin asignar"} disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel>
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Monto Actual
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    value={validateMontoTasa(declaracion?.montoTasa)
                      ? `$${declaracion.montoTasa}`
                      : "Monto de tasa inválido"}
                    disabled
                  />
                </Grid>

                {/* Campos editables */}
                <Grid item xs={12}>
                  <SoftTypography variant="h6" fontWeight="bold" color="dark" sx={{ mt: 2 }}>
                    Actualizar Tasa
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="tasa">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nueva Tasa
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Select
                    fullWidth
                    id="tasa"
                    name="tasa"
                    value={formData.tasa}
                    onChange={handleChanges}
                    size="small"
                    sx={{ height: "40px" }}
                  >
                    {tasasDisponibles.map((tasa) => (
                      <MenuItem key={tasa.value} value={tasa.value}>
                        {tasa.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="montoTasa">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nuevo Monto de Tasa
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    id="montoTasa"
                    name="montoTasa"
                    placeholder="Ingrese el nuevo monto de la tasa"
                    type="number"
                    value={formData.montoTasa}
                    onChange={handleChanges}
                    error={!validateMontoTasa(formData.montoTasa)}
                  />
                  {!validateMontoTasa(formData.montoTasa) && (
                    <SoftTypography variant="caption" color="error" role="alert">
                      Ingrese un monto de tasa válido
                    </SoftTypography>
                  )}
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose}>
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={
                  loadingSave ||
                  !formData.tasa ||
                  !validateMontoTasa(formData.montoTasa)
                }
                onClick={handleSave}
                sx={{ ml: 1 }}
              >
                {loadingSave ? (
                  <CircularProgress size={15} sx={{ marginRight: 0.5 }} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Actualizar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCambiarTasa.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  declaracion: PropTypes.object,
};
